import { convertSession } from "@/components/analysis/chat/v2/utils/converters";
import { streamChatMessage } from "@/components/analysis/chat/v2/utils/streamChatMessage";
import { AGENT_EXECUTOR_CHAT_SUGGESTIONS } from "@/components/mission-control/constants";
import type { ResearchLibraryChatBackend } from "@/components/mission-control/researchLibraryChatBackends";
import { zodBrandedUuid } from "@/utils/helpers";
import { deleteJson, getJson, postJson } from "@/utils/routeUtils";
import type { AnalysisChatV2Id, AnalysisChatV2MessageId } from "@/utils/types";
import { v4 as uuidv4 } from "uuid";

/** Org chats answered by the AgentExecutor. */
export const workspaceChatBackend: ResearchLibraryChatBackend<
  AnalysisChatV2Id,
  AnalysisChatV2MessageId
> = {
  listQueryKey: "research-library-agent-chats",
  emptyStateDescription:
    "I can help understand your research across studies, find quotes, build charts and highlight reels, and more. Start with a suggestion or ask below.",
  suggestions: AGENT_EXECUTOR_CHAT_SUGGESTIONS,
  listChats: async (workspaceId) => {
    const result = await postJson("/api/research-library/agent-chats", {
      orgId: workspaceId,
    });
    if (!result.success) {
      throw new Error(result.error ?? "Failed to fetch chats");
    }
    return result.data.chats;
  },
  deleteChat: async (chatId) => {
    const result = await deleteJson("/api/analysis-chat/update", { chatId });
    if (!result.success) {
      throw new Error(result.error ?? "Failed to delete chat");
    }
  },
  parseChatId: (value) =>
    zodBrandedUuid<AnalysisChatV2Id>().safeParse(value).data ?? null,
  newChatId: () => zodBrandedUuid<AnalysisChatV2Id>().parse(uuidv4()),
  fetchChat: async (chatId) => {
    const result = await getJson("/api/analysis/chat/v2/load", { chatId });
    return result.success && result.data ? convertSession(result.data) : null;
  },
  sendMessage: ({ orgId, surveyIds, ...args }) =>
    streamChatMessage({
      ...args,
      type: "analysis-chat-v2",
      owner: { type: "org", orgId, surveyIds },
    }),
};
